import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';

import BASE_URL from '../../config';
import { fetchWithAuth } from '../../utils/authUtils';
import {
  getLibraryFilterSnapshotBucket,
  LIBRARY_FILTER_SNAPSHOT_BUCKET_ALL,
  LIBRARY_FILTER_SNAPSHOT_BUCKET_FREE,
  normalizeLibraryAccessType,
} from '../../utils/libraryAccessType';
import { readErrorResponse } from '../../utils/readErrorResponse';


export const FILTER_SNAPSHOT_BUCKET_ALL = LIBRARY_FILTER_SNAPSHOT_BUCKET_ALL;
export const FILTER_SNAPSHOT_BUCKET_FREE = LIBRARY_FILTER_SNAPSHOT_BUCKET_FREE;


export const getFilterSnapshotBucket = getLibraryFilterSnapshotBucket;


const normalizeFilterSnapshotCameraEntry = (cameraEntry) => {
  const {
    lensSeriesGroups: lensSeriesGroupsCamel,
    lens_series_groups: lensSeriesGroupsSnake,
    ...cameraEntryRest
  } = cameraEntry || {};

  const rawLensSeriesGroups = Array.isArray(lensSeriesGroupsCamel)
    ? lensSeriesGroupsCamel
    : Array.isArray(lensSeriesGroupsSnake)
      ? lensSeriesGroupsSnake
      : [];

  // Group flat lens_series_groups by brand to build the lensBrands hierarchy
  const brandMap = new Map();
  rawLensSeriesGroups.forEach((lensSeriesGroup) => {
    const brand = lensSeriesGroup?.brand || "";
    const brandKey = brand.toLowerCase().trim() || "unknown";
    if (!brandMap.has(brandKey)) {
      brandMap.set(brandKey, { brand, seriesGroups: [] });
    }
    brandMap.get(brandKey).seriesGroups.push({
      key: lensSeriesGroup?.key || "",
      brand: lensSeriesGroup?.brand || "",
      series: lensSeriesGroup?.series || "",
      label: lensSeriesGroup?.series || lensSeriesGroup?.label || "",
      lenses: Array.isArray(lensSeriesGroup?.lenses) ? lensSeriesGroup.lenses : [],
    });
  });

  return {
    ...cameraEntryRest,
    lensBrands: Array.from(brandMap.values()),
  };
};


const normalizeFilterSnapshot = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') {
    return {
      manufacturers: [],
    };
  }

  const manufacturers = Array.isArray(snapshot.manufacturers)
    ? snapshot.manufacturers
    : [];

  return {
    ...snapshot,
    manufacturers: manufacturers.map((manufacturer) => ({
      ...manufacturer,
      cameras: Array.isArray(manufacturer?.cameras)
        ? manufacturer.cameras.map(normalizeFilterSnapshotCameraEntry)
        : [],
    })),
  };
};


const createFilterBucketState = () => ({
  [FILTER_SNAPSHOT_BUCKET_ALL]: null,
  [FILTER_SNAPSHOT_BUCKET_FREE]: null,
});


const initialState = {
  products: [],
  filterSnapshots: createFilterBucketState(),
  status: 'idle',
  filterStatusByAccessType: {
    [FILTER_SNAPSHOT_BUCKET_ALL]: 'idle',
    [FILTER_SNAPSHOT_BUCKET_FREE]: 'idle',
  },
  filterErrorsByAccessType: createFilterBucketState(),
  error: null,
  currentProductsRequestId: null,
  currentFilterRequestIdByAccessType: createFilterBucketState(),
};


export const fetchLibraryFilterOptions = createAsyncThunk(
  'products/fetchLibraryFilterOptions',
  async ({ accessType } = {}, { rejectWithValue, signal }) => {
    try {
      const bucket = getFilterSnapshotBucket(accessType);
      const accessToken = Cookies.get('access_token');
      const requestUrl = new URL(`${BASE_URL}/products/v1/approved-products/filter-options/`);
      if (bucket === FILTER_SNAPSHOT_BUCKET_FREE) {
        requestUrl.searchParams.set('access_type', FILTER_SNAPSHOT_BUCKET_FREE);
      }

      let response;
      if (accessToken) {
        response = await fetchWithAuth(requestUrl.toString(), {
          method: 'GET',
          signal,
        });
      } else {
        response = await fetch(requestUrl.toString(), {
          method: 'GET',
          signal,
        });
      }

      if (!response.ok) {
        const errorDetails = await readErrorResponse(response);
        return rejectWithValue({ bucket, error: errorDetails });
      }

      return {
        bucket,
        payload: await response.json(),
      };
    } catch (error) {
      return rejectWithValue({
        bucket: getFilterSnapshotBucket(accessType),
        error: error.message,
      });
    }
  }
);


export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (
    {
      page,
      pageSize,
      camera,
      lensSeries,
      accessType,
      full_price__gte,
      full_price__lte,
      search,
      product,
      sort,
    },
    { rejectWithValue, signal }
  ) => {
    try {
      const queryParams = new URLSearchParams({
        page,
        page_size: pageSize,
      });
      const normalizedAccessType = normalizeLibraryAccessType(accessType);
      if (normalizedAccessType) {
        queryParams.set('access_type', normalizedAccessType);
      }
      if (search) {
        queryParams.set('search', search);
      }
      if (sort) {
        queryParams.set('sort', sort);
      }

      if (full_price__gte) {
        queryParams.append('full_price__gte', full_price__gte);
      }
      if (full_price__lte) {
        queryParams.append('full_price__lte', full_price__lte);
      }

      if (product && product.length > 0) {
        product.forEach((prod) => {
          queryParams.append('product', prod);
        });
      } else {
        if (camera && camera.length > 0) {
          camera.forEach((cam) => {
            queryParams.append('camera', cam);
          });
        }

        if (lensSeries && lensSeries.length > 0) {
          lensSeries.forEach((seriesKey) => {
            queryParams.append('lens_series', seriesKey);
          });
        }
      }

      const queryString = queryParams.toString();
      const accessToken = Cookies.get('access_token');
      let response;
      if (accessToken) {
        response = await fetchWithAuth(`${BASE_URL}/products/v1/approved-products/?${queryString}`, {
          method: 'GET',
          signal,
        });
      } else {
        response = await fetch(`${BASE_URL}/products/v1/approved-products/?${queryString}`, {
          method: 'GET',
          signal,
        });
      }

      if (!response.ok) {
        const errorDetails = await readErrorResponse(response);
        return rejectWithValue(errorDetails);
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state, action) => {
        state.status = 'loading';
        state.currentProductsRequestId = action.meta.requestId;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        if (state.currentProductsRequestId !== action.meta.requestId) {
          return;
        }
        state.status = 'succeeded';
        state.products = action.payload;
        state.currentProductsRequestId = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        if (state.currentProductsRequestId !== action.meta.requestId) {
          return;
        }
        if (action.meta.aborted) {
          state.status = state.products?.results ? 'succeeded' : 'idle';
          state.currentProductsRequestId = null;
          return;
        }
        state.status = 'failed';
        state.error = action.payload || action.error.message;
        state.currentProductsRequestId = null;
      })
      .addCase(fetchLibraryFilterOptions.pending, (state, action) => {
        const bucket = getFilterSnapshotBucket(action.meta.arg?.accessType);
        state.filterStatusByAccessType[bucket] = 'loading';
        state.currentFilterRequestIdByAccessType[bucket] = action.meta.requestId;
        state.filterErrorsByAccessType[bucket] = null;
      })
      .addCase(fetchLibraryFilterOptions.fulfilled, (state, action) => {
        const { bucket, payload } = action.payload;
        if (state.currentFilterRequestIdByAccessType[bucket] !== action.meta.requestId) {
          return;
        }
        state.filterSnapshots[bucket] = normalizeFilterSnapshot(payload);
        state.filterStatusByAccessType[bucket] = 'succeeded';
        state.currentFilterRequestIdByAccessType[bucket] = null;
      })
      .addCase(fetchLibraryFilterOptions.rejected, (state, action) => {
        const bucket = action.payload?.bucket || getFilterSnapshotBucket(action.meta.arg?.accessType);
        if (state.currentFilterRequestIdByAccessType[bucket] !== action.meta.requestId) {
          return;
        }
        if (action.meta.aborted) {
          state.filterStatusByAccessType[bucket] = state.filterSnapshots[bucket] ? 'succeeded' : 'idle';
          state.currentFilterRequestIdByAccessType[bucket] = null;
          return;
        }
        state.filterStatusByAccessType[bucket] = 'failed';
        state.filterErrorsByAccessType[bucket] = action.payload?.error || action.error.message;
        state.currentFilterRequestIdByAccessType[bucket] = null;
      });
  },
});


export default productsSlice.reducer;
